import React, { useState } from 'react';
import { Select, SelectProps, Divider } from 'antd';
import SearchInput from './SearchInput';
import {
  legacyBorder,
  legacyFillColor,
  legacyTextColor,
} from '@/utils/legacyColor';

const { Option } = Select;

interface OptionType {
  id?: string;
  sensor_id?: string;
  courier_id?: string;
  name?: string;
  disabled?: boolean;
  // Allow dynamic property access via label prop. TODO: properly type this!
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface CustomDynamicSelectProps extends Omit<
  SelectProps,
  'style' | 'options'
> {
  width?: string;
  height?: string;
  border?: string;
  bgColor?: string;
  textColor?: string;
  borderRadius?: string;
  lineHeight?: string;
  boxShadow?: string;
  placeholderColor?: string;
  options?: OptionType[];
  label?: string;
  emptyContent?: React.ReactNode;
}

const CustomDynamicSelect: React.FC<CustomDynamicSelectProps> = ({
  allowClear = false,
  showSearch = false,
  mode,
  width,
  height,
  border,
  bgColor,
  textColor,
  borderRadius,
  lineHeight,
  placeholderColor: _placeholderColor,
  value,
  options,
  boxShadow,
  disabled,
  label = 'name',
  emptyContent,
  ...props
}) => {
  const [searchText, setSearchText] = useState('');
  const [searchInputKey, setSearchInputKey] = useState(0);
  const filteredOptions =
    showSearch && searchText
      ? options?.filter((option) =>
          String(option[label] ?? '')
            .toLowerCase()
            .includes(searchText.trim().toLowerCase())
        )
      : options;

  const customStyle = {
    height: height || '36px',
    width: width || '100%',
    border: legacyBorder(border) || '1px solid var(--zl-border-soft, #d9d9d9)',
    backgroundColor: legacyFillColor(bgColor) || 'var(--surface-container)',
    color: legacyTextColor(textColor) || 'var(--text-primary)',
    borderRadius: borderRadius || '8px',
    lineHeight: lineHeight || 'normal',
    boxShadow: boxShadow || 'none',
  };

  return (
    <Select
      showSearch={false}
      allowClear={allowClear}
      mode={mode}
      value={value}
      placeholder='Select'
      style={{ ...customStyle }}
      disabled={disabled}
      onDropdownVisibleChange={(open) => {
        if (!open) {
          setSearchText('');
          setSearchInputKey((key) => key + 1);
        }
      }}
      dropdownRender={
        showSearch
          ? (menu) => (
              <>
                <div style={{ padding: 8 }}>
                  <SearchInput
                    key={searchInputKey}
                    placeholder='Search'
                    width='100%'
                    onChange={setSearchText}
                    onClear={() => setSearchText('')}
                  />
                </div>
                <Divider style={{ margin: '4px 0' }} />
                {menu}
              </>
            )
          : undefined
      }
      {...props}
      notFoundContent={
        options && options.length === 0
          ? emptyContent
          : filteredOptions && filteredOptions.length === 0
            ? 'No matches found'
            : undefined
      }
    >
      {filteredOptions &&
        filteredOptions.map((option, index) => (
          <Option
            key={option.id || option.sensor_id || option.courier_id || index}
            disabled={option?.disabled}
            value={option.id ?? option.sensor_id ?? option.courier_id}
          >
            {option[label]}
          </Option>
        ))}
    </Select>
  );
};

export default CustomDynamicSelect;
